import { BatteryCharging, ChevronRight, Sun } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { useSiteContent } from "@/hooks/useSiteContent";

const solarSeoPages = [
  { label: "Solar Installation California", path: "/solar-installation-california", summary: "Target installation keywords with a dedicated California service page." },
  { label: "Solar Repair California", path: "/solar-repair-california", summary: "Capture solar maintenance, troubleshooting, and panel repair searches." },
  { label: "Battery Storage California", path: "/battery-storage-california", summary: "Create a focused page for backup power and energy storage intent." },
  { label: "California Service Areas", path: "/california-service-areas", summary: "Strengthen local SEO with a dedicated California service area page." },
];

export default function SolarPage() {
  const { siteData } = useSiteContent();

  return (
    <div className="space-y-24 pb-6" data-testid="solar-page">
      <PageHero description="Solar planning, panel support, and battery-ready solutions for properties that want stronger efficiency and more control over power costs." eyebrow="Solar services" imageUrl={siteData.solar_page.hero_image} phone={siteData.phone} primaryHref="#solar-quote-form" primaryLabel="Request solar quote" testIdPrefix="solar-hero" title="Custom solar systems designed around your roof and energy use." />

      <section className="px-4 md:px-6" data-testid="solar-services-detail-section">
        <div className="page-section space-y-10">
          <SectionHeading description="From installation through monitoring support, this page gives homeowners a clear view of what Monarch can handle." eyebrow="What we offer" testIdPrefix="solar-sections-heading" title="Practical solar services that support savings, resilience, and roof compatibility." />
          <div className="grid gap-6 lg:grid-cols-3">
            {siteData.solar_page.sections.map((section, index) => (
              <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid={`solar-service-card-${index + 1}`} key={section.title}>
                <CardContent className="space-y-4 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {index === 2 ? <BatteryCharging className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`solar-service-title-${index + 1}`}>
                    {section.title}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`solar-service-description-${index + 1}`}>
                    {section.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="solar-benefits-section">
        <div className="page-section grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div className="image-shell aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="solar-benefits-image-shell">
            <img alt="Solar technicians working" className="h-full w-full object-cover" data-testid="solar-benefits-image" src="https://images.pexels.com/photos/11644973/pexels-photo-11644973.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
          </div>
          <div className="space-y-6 rounded-[2rem] bg-white/80 p-6 shadow-[0_18px_70px_rgba(16,24,40,0.08)] md:p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary" data-testid="solar-benefits-eyebrow">
              Benefits of solar
            </p>
            <h2 className="text-4xl font-bold text-foreground" data-testid="solar-benefits-title">
              Lower bills today, stronger energy confidence for tomorrow.
            </h2>
            <div className="space-y-4">
              {siteData.solar_page.benefits.map((benefit, index) => (
                <div className="flex items-center gap-3 rounded-[1.2rem] border border-primary/10 bg-primary/5 px-4 py-4" data-testid={`solar-benefit-item-${index + 1}`} key={benefit}>
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <p className="text-sm text-foreground">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="solar-seo-pages-section">
        <div className="page-section space-y-10">
          <SectionHeading description="These new service and location pages turn the website into a stronger multi-page California solar business with clearer search intent coverage." eyebrow="SEO-ready solar pages" testIdPrefix="solar-seo-pages-heading" title="Dedicated California pages for installation, repair, storage, and local service areas." />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {solarSeoPages.map((page) => (
              <Card className="rounded-[2rem] border-white/60 bg-white/92" data-testid={`solar-seo-page-card-${page.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={page.path}>
                <CardContent className="space-y-4 p-6">
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`solar-seo-page-title-${page.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {page.label}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`solar-seo-page-summary-${page.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {page.summary}
                  </p>
                  <Button asChild className="rounded-full px-5" variant="outline">
                    <Link data-testid={`solar-seo-page-link-${page.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} to={page.path}>
                      Open page
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="solar-quote-section" id="solar-quote-form">
        <div className="page-section">
          <LeadForm buttonLabel="Request solar quote" description="Tell us about your roof, power goals, or storage interest and we’ll guide the next step." leadType="quote" pageSource="solar-quote" testIdPrefix="solar-quote" title="Talk with our solar team" />
        </div>
      </section>
    </div>
  );
}